import * as Yup from 'yup';

import { FormikErrors, FormikTouched } from 'formik';

import { EntityFieldType } from 'utils/api/modules/entities/entities.types';
import { MAX_API_KEY_LENGTH, ProtectorType } from 'core/constants';

import { colors } from 'theme/colors';

export type EntityFieldError = {
  key?: string;
  name?: string;
  protectorType?: string;
  internalNote?: string;
  helpText?: string;
  properties?: {
    enum?: string;
    lookupType?: string;
  };
};

export type EntityFieldTypeWithoutUUID = Omit<EntityFieldType, 'uuid'>;

const NOT_ALLOWED_PROTECTOR_TYPES = [
  ProtectorType.Section,
  ProtectorType.MultiChoice,
  ProtectorType.Image,
  ProtectorType.RiskFactor,
  ProtectorType.Stage,
  ProtectorType.Pipeline,
  ProtectorType.Attachment,
  ProtectorType.Signature,
  ProtectorType.RepeatingGroup,
  // a question type, not a field type
  ProtectorType.YesNoNa,
];

export const possibleProtectorTypes = Object.values(ProtectorType).filter(
  (i) => !NOT_ALLOWED_PROTECTOR_TYPES.includes(i)
);

// should allow for numbers, letters, and underscore
const camelCaseRegex = /^[a-z0-9_]+([A-Z0-9][a-z0-9]*)*$/;

export const EntityFiledValidationSchema = Yup.object().shape({
  name: Yup.string().required('This field is required'),
  key: Yup.string().matches(camelCaseRegex, 'Key must be in camel case'),
  protectorType: Yup.string().required('This field is required'),
  internalNote: Yup.string().nullable(),
  helpText: Yup.string().nullable(),
});

export const getBorderStyle = (
  editing: boolean,
  errors: FormikErrors<EntityFieldTypeWithoutUUID>,
  touched: FormikTouched<EntityFieldTypeWithoutUUID>
): string => {
  if (editing) {
    return '1px solid rgba(0, 0, 0, 0.06)';
  }
  if (errors.protectorType && touched.protectorType) {
    return '2px solid rgba(239, 83, 80, 1)';
  }
  return `1px solid ${colors.grey[300]}`;
};

export function modifyInputValue(value: string, prefixLength: number = 0): string {
  const maxLength = MAX_API_KEY_LENGTH - prefixLength;
  // transform the value to camel case and remove all special characters and add prefix with module name

  const formattedValue = value
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => (index === 0 ? word.toLowerCase() : word.toUpperCase()))
    .replace(/\s+/g, '')
    // remove all special characters
    .replace(/[^a-zA-Z0-9]/g, '');

  const valueLength = formattedValue.length;

  if (valueLength > maxLength) {
    return formattedValue.slice(0, maxLength);
  }

  return formattedValue;
}
